import { FormManager } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';
import difference from 'lodash/difference';
import union from 'lodash/union';

import { Errors } from '../../common/helpers';
import { Permission } from '../../__generated__/graphql';
import { useSession } from '../Auth/SessionProvider';
import UI from '../../common/UI';

export interface EditPermissionsFormProps {
  form: FormManager<{ permissions: Permission[]; }>;
  errors?: Errors;
  readonly?: boolean;
  inputLabel?: string;
  inputDescription?: string;
}

export const EditUserPermissionsForm = ({
  form, errors, readonly = false, inputLabel, inputDescription,
}: EditPermissionsFormProps) => {
  const { t } = useTranslation();
  const { userPermissions: availablePermissions, implicitPermissions } = useSession();

  const userRole = form.values.permissions.includes(Permission.admin) ? 'admin' : 'user';

  const setAdmin = () => form.set('permissions', availablePermissions);
  const setUser = (permissions: Permission[] = []) => form.set('permissions', permissions);

  return (
    <>
      <UI.InputGroup sc={{ valid: userRole === 'user' || !errors.permissions, touched: form.touched.permissions }}>
        <UI.InputLabel>
          {t('user_role')}
          {' '}
          <UI.RequiredMark />
        </UI.InputLabel>
        <UI.Radio
          checked={userRole === 'user'}
          onChange={() => setUser()}
          sc={{ box: true, block: true, mb: 1 }}
        >
          <UI.Strong sc={{ block: true }}>
            {t('normal_user')}
          </UI.Strong>
          {t('normal_user_role_description')}
        </UI.Radio>
        <UI.Radio
          checked={userRole === 'admin'}
          onChange={() => setAdmin()}
          sc={{ box: true, block: true }}
        >
          <UI.Strong sc={{ block: true }}>
            {t('administrator')}
          </UI.Strong>
          {t('admin_role_description')}
        </UI.Radio>
        {userRole === 'admin' && <UI.ErrorMessages attribute={t('user_role')} errors={errors.permissions} />}
      </UI.InputGroup>
      {userRole === 'user' && (
        <EditPermissionsForm
          form={form}
          errors={errors}
          readonly={readonly}
          inputLabel={inputLabel}
          inputDescription={inputDescription}
          availablePermissions={availablePermissions}
          implicitPermissions={implicitPermissions}
        />
      )}
    </>
  );
};

export const EditAppPermissionsForm = ({
  form, errors, readonly = false, inputLabel, inputDescription,
}: EditPermissionsFormProps) => {
  const { appPermissions: availablePermissions, implicitPermissions } = useSession();

  return (
    <EditPermissionsForm
      form={form}
      errors={errors}
      readonly={readonly}
      inputLabel={inputLabel}
      inputDescription={inputDescription}
      availablePermissions={availablePermissions}
      implicitPermissions={implicitPermissions}
    />
  );
};

interface KnowsPermissions {
  availablePermissions: Permission[];
  implicitPermissions: { [key in Permission]?: Permission[] };
}

const EditPermissionsForm = ({
  form, errors, readonly, availablePermissions, implicitPermissions,
  inputLabel, inputDescription,
}: EditPermissionsFormProps & KnowsPermissions) => {
  const { t } = useTranslation();

  const hasPermission = (permission: Permission, list: Permission[]) => list.indexOf(permission) > -1;

  const togglePermission = (permission: Permission) => {
    form.set('permissions', (values: Permission[]) => {
      const newValues = hasPermission(permission, values)
        ? difference(values, [permission])
        : union(values, [permission, ...implicitPermissions[permission] || []]);

      return newValues.filter((value) => !isDisabled(value, newValues));
    });
  };

  const fullUserPermissions = availablePermissions.filter((permission) => permission !== Permission.admin);

  /**
   * A permission is disabled if it does not have ALL of its dependent permissions.
   */
  const isDisabled = (permission: Permission, list: Permission[]) => implicitPermissions[permission] && (
    implicitPermissions[permission].reduce((invalid, key: Permission) => invalid || !hasPermission(key, list), false)
  );

  const setPermissions = (permissions: Permission[] = []) => form.set('permissions', permissions);

  const indents: Record<string, number> = {
    [Permission.edit_registrations]: 1,
    [Permission.participant_logistics]: 1,
    [Permission.refunds]: 2,
    [Permission.exports]: 1,
    [Permission.messages]: 1,
  };

  return (
    <UI.InputGroup sc={{ valid: !errors.permissions, touched: form.touched.permissions }}>
      {readonly && (
        <UI.InputLabel>
          {inputLabel || t('permissions_title')}
        </UI.InputLabel>
      )}
      {!readonly && (
        <>
          <UI.InputLabel>
            {inputLabel || t('permissions_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {inputDescription || t('select_permissions_description')}
          </UI.InputDescription>
          <UI.Div sc={{ my: 1 }}>
            <UI.A
              onClick={() => setPermissions(fullUserPermissions)}
              sc={{ disabled: form.values.permissions.length === fullUserPermissions.length }}
            >
              {t('select_all')}
            </UI.A>
            <UI.Span sc={{ muted: true }}> • </UI.Span>
            <UI.A
              onClick={() => setPermissions([])}
              sc={{ disabled: form.values.permissions.length === 0 }}
            >
              {t('deselect_all')}
            </UI.A>
          </UI.Div>
        </>
      )}
      <UI.FormGrid sc={{ gutter: 0.25 }}>
        {fullUserPermissions.map((permission) => (
          <UI.Checkbox
            checked={hasPermission(permission, form.values.permissions)}
            onChange={() => togglePermission(permission)}
            disabled={isDisabled(permission, form.values.permissions) || readonly}
            key={permission}
            sc={{ ml: (indents[permission] ?? 0) * 4 }}
          >
            <UI.Span sc={{ block: true, fontWeight: 500 }}>
              {t(`permissions.${permission}`)}
            </UI.Span>
            <UI.Span sc={{ color: 'gray.500' }}>
              {t(`permissions.${permission}_description`)}
            </UI.Span>
          </UI.Checkbox>
        ))}
      </UI.FormGrid>
      <UI.ErrorMessages attribute={t('permissions_title')} errors={errors.permissions} />
    </UI.InputGroup>
  );
};

export default undefined;
